import React, { useCallback } from 'react';
import type { MenuProps } from 'antd';
import { Link, LinkProps, useLocation } from 'react-router-dom';
import { createPath } from 'history';
import { useTransitionNavigation } from '../../~reusables/contexts/TransitionContext';

/**
 * Click handler for an `<a href={to}>` (or an antd `<Button href={to}>`) that
 * navigates in-app with a transition. Modified and non-primary clicks (new
 * tab, new window, etc.) are left to the browser.
 */
export const useTransitionLinkClick = <T extends HTMLElement>(
  to: string,
  onClick?: React.MouseEventHandler<T>,
) => {
  const { navigateWithTransition } = useTransitionNavigation();
  const location = useLocation();
  const currentPath = createPath(location);

  return useCallback(
    (e: React.MouseEvent<T>) => {
      // Allow default behavior for modified clicks (new tab, etc.)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
        return;
      }

      e.preventDefault();

      // Call any existing onClick handler
      onClick?.(e);

      // Navigate with transition, without stacking a duplicate history entry
      if (to !== currentPath) navigateWithTransition(to);
    },
    [to, onClick, currentPath, navigateWithTransition],
  );
};

/**
 * antd menu `onClick` for menus whose items are links. When the item itself
 * has keyboard focus, Enter reaches the menu instead of the link, so follow
 * the link. Clicks, and Enter on a focused link, already go to the link.
 */
export const followMenuItemLink: NonNullable<MenuProps['onClick']> = ({
  domEvent,
}) => {
  const target = domEvent.target as HTMLElement;
  if (domEvent.type !== 'keydown' || target.closest('a')) return;
  target.querySelector('a')?.click();
};

interface TransitionLinkProps extends Omit<LinkProps, 'to'> {
  to: string;
  children: React.ReactNode;
}

/**
 * A Link component that uses React's useTransition to keep the old UI
 * visible while the new route loads. This prevents flashes of loading states.
 */
export const TransitionLink: React.FC<TransitionLinkProps> = ({
  to,
  children,
  onClick,
  ...props
}) => {
  const handleClick = useTransitionLinkClick(to, onClick);

  return (
    <Link to={to} onClick={handleClick} {...props}>
      {children}
    </Link>
  );
};

export default TransitionLink;
